<template>
    <div>
        <kgr-loading v-if="loadingContent" />
        <!-- TinyMCE cannot restyle a running instance: on a skin change the editor is torn down and
             rebuilt inside this wrapper, in two ticks, so Vue never patches around TinyMCE's own DOM -->
        <div v-if="editorMounted">
            <editor
                :id="editorId"
                :value="value"
                :init="initEditor(this)"
                @input="onContentInput"
                @onInit="loadingContent = false"
            />
        </div>
    </div>
</template>

<script>
import 'tinymce/tinymce';
import 'tinymce/themes/silver';
import 'tinymce/icons/default/icons';
import 'tinymce/plugins/link/plugin';
import 'tinymce/plugins/lists/plugin';
import 'tinymce/plugins/pagebreak/plugin';
import 'tinymce/plugins/table/plugin';
import 'tinymce/models/dom';
import Editor from '@tinymce/tinymce-vue';
import tinymce from 'tinymce';
import Loading from './common/Loading';
import { isDarkTheme, observeDarkTheme } from '../js/service/ColorScheme';

export default {
    components: {
        'kgr-loading': Loading,
        editor: Editor,
    },
    props: {
        value: {
            validator: (prop) => typeof prop === 'string' || prop === null,
            required: true,
        },
        locale: {
            type: String,
            required: false,
            default: 'de_DE',
        },
        editorId: {
            type: String,
            required: false,
            default: 'kgreditor',
        },
        height: {
            type: String,
            required: false,
            default: '500px',
        },
        width: {
            type: String,
            required: false,
            default: '100%',
        },
        mini: {
            type: Boolean,
            required: false,
            default: false,
        },
        fontSizeUnit: {
            type: String,
            required: false,
            default: 'pt',
        },
    },
    data() {
        return {
            loadingContent: true,
            dark: isDarkTheme(),
            editorMounted: true,
            fonts: [
                'Arial',
                'Archivo Narrow',
                'Arimo',
                'Assistant',
                'Baloo Tammudu',
                'Barlow Semi Condensed',
                'Cardo',
                'Cormorant',
                'Cuprum',
                'Darker Grotesque',
                'Didact Gothic',
                'Harmattan',
                'Heebo',
                'Kalam',
                'Krub',
                'Lato',
                'Montserrat',
                'Noto Sans',
                'Open Sans',
                'Roboto',
                'Sans-Serif',
                'Serif',
                'Source Sans Pro',
                'Titillium Web',
            ],
            fontSizeFormats: `8${this.fontSizeUnit
            } 10${this.fontSizeUnit
            } 12${this.fontSizeUnit
            } 14${this.fontSizeUnit
            } 16${this.fontSizeUnit
            } 18${this.fontSizeUnit
            } 20${this.fontSizeUnit
            } 24${this.fontSizeUnit
            } 36${this.fontSizeUnit}`,
        };
    },
    computed: {
        skin() {
            return this.dark ? 'oxide-dark' : 'oxide';
        },
        fontTypes() {
            let fontString = '';
            this.fonts.forEach((font) => {
                fontString += `${font}=${font};`;
            });
            return fontString;
        },

    },
    watch: {
        async dark() {
            // the editor is rebuilt from the value prop, so the parent has to hold what was typed
            const editor = tinymce.get(this.editorId);
            if (editor) {
                this.onContentInput(editor.getContent());
            }
            this.editorMounted = false;
            this.loadingContent = true;
            await this.$nextTick();
            this.editorMounted = true;
        },
    },
    mounted() {
        this.stopThemeWatch = observeDarkTheme((dark) => {
            this.dark = dark;
        });
        // disable tooltips for toolbar's menu items
        $(document).on('mouseover', '.tox-collection__group', (event) => {
            event.target.title = '';
        });
    },
    beforeUnmount() {
        this.stopThemeWatch();
    },
    methods: {
        onContentInput(content) {
            this.$emit('input', content);
            this.$nextTick(() => {
                this.$emit('post-input', content);
            });
        },
        initEditor(kgrEditor, height = kgrEditor.height) {
            return {
                license_key: 'gpl',
                style_formats_merge: true,
                entity_encoding: 'raw',
                height,
                width: kgrEditor.width,
                skin: kgrEditor.skin,
                skin_url: `/css/tinymce/${kgrEditor.skin}`,
                menubar: false,
                language: kgrEditor.locale !== 'en_GB' ? kgrEditor.locale : null,
                language_url: kgrEditor.locale !== 'en_GB' ? (`/css/tinymce/langs/${kgrEditor.locale}.js`) : null,
                statusbar: false,
                formats: {
                    underline: { inline: 'u' },
                },
                font_size_formats: kgrEditor.fontSizeFormats,
                font_family_formats: kgrEditor.fontTypes,
                block_formats: 'Paragraph=p;Header 1=h1;Header 2=h2;Header 3=h3;Header 4=h4;Header 5=h5;Header 6=h6',
                extended_valid_elements: 'span[contenteditable|align<center?justify?left?right|class|dir<ltr?rtl|id|lang|onclick|ondblclick|onkeydown|onkeypress|onkeyup|onmousedown|onmousemove|onmouseout|onmouseover|onmouseup|style|title]',
                content_css: kgrEditor.dark ? ['/css/tinymce/content-dark/content.min.css', '/css/fonts.css'] : '/css/fonts.css',
                content_style: `body { font-family: Arial; font-size: 12${kgrEditor.fontSizeUnit};}`,
                plugins: ['link', 'lists'],
                pagebreak_separator: '<pagebreak>',
                contextmenu: false,
                toolbar_mode: 'wrap',
                table_toolbar: 'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
                init_instance_callback(editor) {
                    // disable iframe's tooltip "Rich-Text-Bereich. Drücken Sie Alt+0 für Hilfe."
                    editor.iframeElement.title = '';

                    /*
                      The following two hacks fix some weirdness with the way the textcolor
                      plugin works - namely, it was attemping to apply color and background-color
                      directly on the element that had the noneditable css class on it instead of putting
                      a span around it as underline does.
                    */
                    editor.formatter.get('forecolor')[0].exact = true;
                    editor.formatter.get('hilitecolor')[0].exact = true;

                    // END workaround
                },
                toolbar: 'styles | bold italic underline forecolor | alignleft aligncenter alignright alignjustify | link bullist numlist outdent indent | fontfamily | fontsize',
            };
        },
    },
};
</script>
